// The scoresheet's layout, without React: the moves paired into numbered
// rows as a scoresheet is written, and what each cell shows (concealed, the
// move a line branches after, the move on the board, its grade). The
// component in `Scoresheet.tsx` only draws what this decides.
import { type San } from '@chess/api-model';
import type { Judgements, SheetGrade } from './judgements.ts';

/** One line of a scoresheet: the move number, White's move, Black's move. */
export interface ScoresheetRow {
  number: number;
  white?: San;
  black?: San;
}

/** Pairs a flat list of SAN moves into scoresheet rows, as a scoresheet is written. */
export function toRows(moves: readonly San[]): ScoresheetRow[] {
  const rows: ScoresheetRow[] = [];
  for (let i = 0; i < moves.length; i += 2) {
    rows.push({ number: i / 2 + 1, white: moves[i], black: moves[i + 1] });
  }
  return rows;
}

/** What the sheet marks, as `ScoresheetProps` carries it. */
export interface CellMarks {
  judgements?: Judgements;
  /** The game ply the reader's own line starts from; the move that led there is marked. */
  branchPly?: number;
  /** Moves from this index on are a puzzle's answer and are hidden. */
  concealFrom?: number;
  /** The ply on the board; the move that led to it is highlighted. */
  currentPly?: number;
}

/** One cell of the sheet: a move, or the empty half of the last row. */
export interface SheetCell {
  /** The move's index in the list, 0-based. */
  index: number;
  /** The ply reached after the move, 1-based: what choosing it goes to. */
  ply: number;
  san?: San;
  /** Hidden as a puzzle's answer: drawn as dots, not a button. */
  concealed: boolean;
  /** The reader's line starts after this move. */
  branch: boolean;
  /** The position after this move is on the board. */
  current: boolean;
  grade?: SheetGrade;
}

export interface SheetRow {
  number: number;
  white: SheetCell;
  black: SheetCell;
}

/** The cell for the move at an index, with its flags. */
export function cellOf(moves: readonly San[], index: number, marks: CellMarks): SheetCell {
  const { judgements, branchPly, concealFrom, currentPly } = marks;
  return {
    index,
    ply: index + 1,
    san: moves[index],
    concealed: concealFrom !== undefined && index >= concealFrom,
    branch: branchPly !== undefined && branchPly > 0 && index === branchPly - 1,
    current: currentPly !== undefined && currentPly > 0 && index === currentPly - 1,
    grade: judgements?.get(index),
  };
}

/** The sheet's rows with every cell's flags. */
export function sheetRows(moves: readonly San[], marks: CellMarks): SheetRow[] {
  return toRows(moves).map((row, i) => ({ number: row.number, white: cellOf(moves, i * 2, marks), black: cellOf(moves, i * 2 + 1, marks) }));
}
